import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

interface User {
  uid: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  mobile?: string;
  status: string;
  role?: string;
  requestedRole?: string;
  requestedAt?: string | null;
  createdAt: string;
}

// Helper function to safely convert Firestore Timestamps to Date
const convertToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  
  // Already a Date object
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }
  
  // Timestamp with toDate method (Firestore SDK)
  if (typeof timestamp.toDate === 'function') {
    try {
      const date = timestamp.toDate();
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }
  
  // Timestamp with seconds property (Firestore REST API format)
  if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
    const seconds = timestamp.seconds;
    const nanoseconds = timestamp.nanoseconds || 0;
    const date = new Date(seconds * 1000 + nanoseconds / 1000000);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // String that can be parsed (including ISO date strings from JSON serialization)
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Number (milliseconds or seconds)
  if (typeof timestamp === 'number') {
    // If it's less than a reasonable date in seconds (year 2000), treat as seconds
    const date = timestamp < 946684800000 ? new Date(timestamp * 1000) : new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  return null;
};

const ManageUsers: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    user: User | null;
    action: "approve" | "reject" | null;
  }>({
    open: false,
    user: null,
    action: null,
  });
  const [selectedRole, setSelectedRole] = useState<"reporter" | "co-editor" | "chief-editor" | "admin">("reporter");
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });

  const loadCounts = useCallback(async () => {
    try {
      const [pendingResult, approvedResult, rejectedResult] = await Promise.all([
        api.listUsers("pending"),
        api.listUsers("approved"),
        api.listUsers("rejected"),
      ]);

      const pendingCount = pendingResult.success ? pendingResult.users?.length || 0 : 0;
      const approvedCount = approvedResult.success ? approvedResult.users?.length || 0 : 0;
      const rejectedCount = rejectedResult.success ? rejectedResult.users?.length || 0 : 0;

      setCounts({
        pending: pendingCount,
        approved: approvedCount,
        rejected: rejectedCount,
        all: pendingCount + approvedCount + rejectedCount,
      });
    } catch (err: any) {
      console.error("Failed to load counts:", err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (selectedStatus === "all") {
        // Load all users
        const [pendingResult, approvedResult, rejectedResult] = await Promise.all([
          api.listUsers("pending"),
          api.listUsers("approved"),
          api.listUsers("rejected"),
        ]);

        const allUsers = [
          ...(pendingResult.success ? pendingResult.users || [] : []),
          ...(approvedResult.success ? approvedResult.users || [] : []),
          ...(rejectedResult.success ? rejectedResult.users || [] : []),
        ];

        setUsers(allUsers);
      } else {
        const result = await api.listUsers(selectedStatus);
        if (result.success && result.users) {
          setUsers(result.users);
        } else {
          setError(result.error || "Failed to load users");
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (user?.role === "admin") {
      loadUsers();
      loadCounts();
    }
  }, [user, loadUsers, loadCounts]);

  const handleStatusChange = (status: "pending" | "approved" | "rejected" | "all") => {
    setSelectedStatus(status);
  };

  const handleAction = (user: User, action: "approve" | "reject") => {
    setActionDialog({
      open: true,
      user,
      action,
    });
    if (action === "approve") {
      // Use existing role if available, otherwise use requestedRole, default to reporter
      setSelectedRole((user.role || user.requestedRole || "reporter") as any);
    } else {
      setRejectionReason("");
    }
  };

  const handleConfirmAction = async () => {
    if (!actionDialog.user || !actionDialog.action) return;

    setProcessing(true);
    try {
      const result = await api.manageUsers({
        userId: actionDialog.user.uid,
        action: actionDialog.action,
        role: actionDialog.action === "approve" ? selectedRole : undefined,
        rejectionReason: actionDialog.action === "reject" ? rejectionReason : undefined,
      });

      if (result.success) {
        setActionDialog({ open: false, user: null, action: null });
        loadUsers();
        loadCounts();
        // Trigger heartbeat refresh for navigation counts
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
      } else {
        setError(result.error || "Failed to process request");
      }
    } catch (err: any) {
      setError(err.message || "Failed to process request");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  if (user?.role !== "admin") {
    return (
      <Alert severity="error">Only administrators can access this page.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Manage Users
      </Typography>

          <Box sx={{ mb: 3, mt: 2, display: "flex", gap: 1 }}>
            <Chip
              label={`Pending (${counts.pending})`}
              color="warning"
              onClick={() => handleStatusChange("pending")}
              variant={selectedStatus === "pending" ? "filled" : "outlined"}
            />
            <Chip
              label={`Approved (${counts.approved})`}
              color="success"
              onClick={() => handleStatusChange("approved")}
              variant={selectedStatus === "approved" ? "filled" : "outlined"}
            />
            <Chip
              label={`Rejected (${counts.rejected})`}
              color="error"
              onClick={() => handleStatusChange("rejected")}
              variant={selectedStatus === "rejected" ? "filled" : "outlined"}
            />
            <Chip
              label={`All (${counts.all})`}
              color="default"
              onClick={() => handleStatusChange("all")}
              variant={selectedStatus === "all" ? "filled" : "outlined"}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Alert severity="info">No users found with {selectedStatus} status.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Requested Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.uid}>
                      <TableCell>
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.displayName || "N/A"}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.mobile || "N/A"}</TableCell>
                      <TableCell>
                        <Chip label={user.requestedRole || user.role || "N/A"} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Always prioritize requestedAt when it exists, otherwise fallback to createdAt
                          const date = convertToDate(user.requestedAt) || convertToDate(user.createdAt);
                          return date ? date.toLocaleDateString() : "N/A";
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {user.status === "pending" && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              onClick={() => handleAction(user, "approve")}
                              sx={{ mr: 1 }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleAction(user, "reject")}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {user.status === "rejected" && (
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            onClick={() => handleAction(user, "approve")}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, user: null, action: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.action === "approve" 
            ? (actionDialog.user?.status === "rejected" ? "Approve Rejected User" : "Approve User")
            : "Reject User"}
        </DialogTitle>
        <DialogContent>
          {actionDialog.user && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                User: {actionDialog.user.email}
              </Typography>
              {actionDialog.action === "approve" ? (
                <TextField
                  fullWidth
                  select
                  label="Assign Role"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as any)}
                  sx={{ mt: 2 }}
                >
                  <MenuItem value="reporter">Reporter</MenuItem>
                  <MenuItem value="co-editor">Co-Editor</MenuItem>
                  <MenuItem value="chief-editor">Chief Editor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Please provide a reason for rejecting this user's request..."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog({ open: false, user: null, action: null })}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={actionDialog.action === "approve" ? "success" : "error"}
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : actionDialog.action === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageUsers;

